import { useRef, useEffect, useCallback, useState } from 'react';
import type {
  PixelData,
  PlayerResponse,
  PaymentStatusResponse,
  GameState,
  FriendInfo,
  CharacterParts,
  BattleTimerResponse,
  Language,
} from '@vikings/types';
import { t } from '@vikings/game-data';
import {
  clearCanvas,
  renderPixels,
  renderPixelText,
  measurePixelText,
  Animator,
  generateCharacterParts,
  generateCharacterPixels,
  generateIdleFrames,
  getCoinFrames,
  getHourglassPixels,
  getLogoPixels,
  getBattlefieldTile,
  getCloud1Pixels,
  getCloud2Pixels,
  getBirdFrames,
  getArrowPixels,
  getDragonFrames,
  getFireParticlePixels,
  getLightningPixels,
} from '@vikings/pixel-engine';

interface GameCanvasProps {
  player: PlayerResponse;
  payment: PaymentStatusResponse;
  gameState: GameState;
  onStateChange: (state: GameState) => void;
  onHeroClick: () => void;
  onFriendClick: (friendName?: string, buffIndex?: number, friendCode?: string) => void;
  onCoinClick: () => void;
  battleTimer: BattleTimerResponse | null;
  language: Language;
  onTimerClick: () => void;
  onLanguageToggle: () => void;
  onInfoClick: () => void;
  onLogoutClick: () => void;
  isModalLoading?: boolean;
}

interface FriendData {
  info: FriendInfo;
  parts: CharacterParts;
  animator: Animator;
}

interface CloudInstance {
  x: number;
  yRatio: number;
  speed: number;
  scale: number;
  type: 1 | 2;
}

interface BirdInstance {
  x: number;
  yRatio: number;
  speedX: number;
  scale: number;
  frame: number;
  timer: number;
  wobbleOffset: number;
}

interface ArrowInstance {
  x: number;
  y: number;
  speed: number;
  scale: number;
}

interface DragonInstance {
  x: number;
  yRatio: number;
  speedX: number;
  scale: number;
  frame: number;
  timer: number;
}

interface FireParticle {
  x: number;
  y: number;
  life: number;
  maxLife: number;
  speed: number;
  scale: number;
}

/**
 * Main game canvas — renders the battlefield, big hero, 0-3 smaller friends, coins, clouds, birds, and UI.
 */
export function GameCanvas({
  player,
  payment,
  onHeroClick,
  onFriendClick,
  onCoinClick,
  battleTimer,
  language,
  onTimerClick,
  onLanguageToggle,
  onInfoClick,
  onLogoutClick,
  isModalLoading = false,
}: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  // Pre-generate character data
  const heroPartsRef = useRef(generateCharacterParts(player.phone));
  const heroAnimRef = useRef<Animator | null>(null);
  const friendsDataRef = useRef<FriendData[]>([]);
  const coinAnimRef = useRef<Animator | null>(null);

  // Hit areas for click detection
  const hitAreasRef = useRef<{
    hero: { x: number; y: number; w: number; h: number };
    friends: Array<{
      name: string;
      buffIndex?: number;
      code?: string;
      x: number;
      y: number;
      w: number;
      h: number;
    }>;
    coin: { x: number; y: number; w: number; h: number };
    timer: { x: number; y: number; w: number; h: number };
    langToggle: { x: number; y: number; w: number; h: number };
    infoBtn: { x: number; y: number; w: number; h: number };
    logoutBtn: { x: number; y: number; w: number; h: number };
  }>({
    hero: { x: 0, y: 0, w: 0, h: 0 },
    friends: [],
    coin: { x: 0, y: 0, w: 0, h: 0 },
    timer: { x: 0, y: 0, w: 0, h: 0 },
    langToggle: { x: 0, y: 0, w: 0, h: 0 },
    infoBtn: { x: 0, y: 0, w: 0, h: 0 },
    logoutBtn: { x: 0, y: 0, w: 0, h: 0 },
  });

  // Cached pixel data
  const [scale, setScale] = useState(3);
  const logoRef = useRef<PixelData>(getLogoPixels());
  const battlefieldRef = useRef<PixelData>(getBattlefieldTile());
  const hourglassRef = useRef<PixelData>(getHourglassPixels());
  const cloud1Ref = useRef<PixelData>(getCloud1Pixels());
  const cloud2Ref = useRef<PixelData>(getCloud2Pixels());
  const birdFramesRef = useRef<PixelData[]>(getBirdFrames());

  // Battle effects assets
  const arrowRef = useRef<PixelData>(getArrowPixels());
  const dragonFramesRef = useRef<PixelData[]>(getDragonFrames());
  const fireParticleRef = useRef<PixelData>(getFireParticlePixels());
  const lightningRef = useRef<PixelData>(getLightningPixels());

  // Battle effects instances
  const arrowsRef = useRef<ArrowInstance[]>([]);
  const dragonsRef = useRef<DragonInstance[]>([]);
  const fireParticlesRef = useRef<FireParticle[]>([]);
  const lightningTimerRef = useRef<number>(0);
  const lightningAlphaRef = useRef<number>(0);

  // Dynamic sky objects: Clouds & Ravens
  const cloudsRef = useRef<CloudInstance[]>([
    { x: 30, yRatio: 0.16, speed: 12, scale: 2.5, type: 2 },
    { x: 220, yRatio: 0.12, speed: 8, scale: 2.0, type: 1 },
    { x: 360, yRatio: 0.22, speed: 15, scale: 3.0, type: 2 },
    { x: -70, yRatio: 0.28, speed: 10, scale: 2.2, type: 1 },
  ]);

  const birdsRef = useRef<BirdInstance[]>([
    { x: 60, yRatio: 0.19, speedX: 42, scale: 2, frame: 0, timer: 0, wobbleOffset: 0 },
    { x: -120, yRatio: 0.26, speedX: 50, scale: 3, frame: 1, timer: 0.05, wobbleOffset: 1.8 },
    { x: 250, yRatio: 0.14, speedX: 32, scale: 2, frame: 2, timer: 0.10, wobbleOffset: 3.5 },
  ]);

  // Initialize animations
  useEffect(() => {
    heroPartsRef.current = generateCharacterParts(player.phone);
    const heroFrames = generateIdleFrames(heroPartsRef.current);
    heroAnimRef.current = new Animator({
      frames: heroFrames,
      fps: 3,
      loop: true,
      pingPong: true,
    });

    // 0 to 3 friends
    const list: FriendInfo[] =
      player.friends && player.friends.length > 0
        ? player.friends.slice(0, 3)
        : player.friend
          ? [player.friend]
          : [];

    friendsDataRef.current = list.map((f, i) => {
      const parts = generateCharacterParts(f.characterSeed || f.name || String(i));
      const frames = generateIdleFrames(parts);
      const animator = new Animator({
        frames,
        fps: 2.2 + i * 0.35,
        loop: true,
        pingPong: true,
      });
      return { info: f, parts, animator };
    });

    const coinFrames = getCoinFrames();
    coinAnimRef.current = new Animator({
      frames: coinFrames,
      fps: 4,
      loop: true,
      pingPong: false,
    });
  }, [player]);

  // Resize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;

      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      // Base responsive scale factor
      const newScale = Math.max(2, Math.floor(Math.min(w, h) / 100));
      setScale(newScale);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Game loop
  const render = useCallback(
    (timestamp: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.imageSmoothingEnabled = false;

      const delta = lastTimeRef.current ? Math.min(0.1, (timestamp - lastTimeRef.current) / 1000) : 0.016;
      lastTimeRef.current = timestamp;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Update animations
      heroAnimRef.current?.update(delta);
      for (const friend of friendsDataRef.current) {
        friend.animator.update(delta);
      }
      if (payment.status !== 'submitted') {
        coinAnimRef.current?.update(delta);
      }

      // ── Ground level: Reduced ground (bottom ~26% of frame) ──
      const groundY = Math.round(h * 0.74);

      // ── 1. Clear and draw atmospheric Nordic sky background ──
      clearCanvas(ctx, w, h, '#0c0f1d');

      const skyGrad = ctx.createLinearGradient(0, 0, 0, groundY);
      skyGrad.addColorStop(0, '#0c0f1d');   // Deep space / night
      skyGrad.addColorStop(0.5, '#171c35'); // Twilight blue
      skyGrad.addColorStop(1, '#253556');   // Horizon haze
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, groundY);

      // Distant mountain silhouettes on horizon
      ctx.fillStyle = '#1c2842';
      ctx.beginPath();
      ctx.moveTo(0, groundY);
      for (let mx = 0; mx <= w; mx += 40) {
        const peakH = 15 + Math.sin(mx * 0.04) * 8 + Math.cos(mx * 0.015) * 6;
        ctx.lineTo(mx, groundY - peakH);
      }
      ctx.lineTo(w, groundY);
      ctx.closePath();
      ctx.fill();

      // ── 2. Draw Clouds (animated drifting) ──
      for (const cloud of cloudsRef.current) {
        cloud.x += cloud.speed * delta;
        const sprite = cloud.type === 1 ? cloud1Ref.current : cloud2Ref.current;
        const cloudW = sprite.width * cloud.scale;
        if (cloud.x > w + 40) {
          cloud.x = -cloudW - 30;
          cloud.yRatio = 0.10 + Math.random() * 0.22;
        }
        renderPixels(ctx, sprite, Math.round(cloud.x), Math.round(h * cloud.yRatio), Math.round(cloud.scale));
      }

      // ── 3. Draw Birds / Ravens (animated flapping & soaring) ──
      for (const bird of birdsRef.current) {
        bird.x += bird.speedX * delta;
        bird.timer += delta;
        if (bird.timer >= 0.14) {
          bird.timer = 0;
          bird.frame = (bird.frame + 1) % 3;
        }
        if (bird.x > w + 50) {
          bird.x = -60;
          bird.yRatio = 0.12 + Math.random() * 0.20;
        }
        const birdY = h * bird.yRatio + Math.sin(timestamp * 0.0035 + bird.wobbleOffset) * 6;
        const birdFrame = birdFramesRef.current[bird.frame] ?? birdFramesRef.current[0]!;
        renderPixels(ctx, birdFrame, Math.round(bird.x), Math.round(birdY), bird.scale);
      }

      // ── 4. Draw Battlefield ground tiles ──
      const tile = battlefieldRef.current;
      const tileScale = Math.max(2, Math.floor(scale * 0.75));
      const tileW = tile.width * tileScale;
      const tileH = tile.height * tileScale;

      for (let tx = 0; tx < w; tx += tileW) {
        for (let ty = groundY; ty < h; ty += tileH) {
          renderPixels(ctx, tile, tx, ty, tileScale);
        }
      }

      // Grass fringe highlight along the horizon
      ctx.fillStyle = '#4a9a35';
      ctx.fillRect(0, groundY, w, 2);

      // ── 5. Draw VIKINGS logo (BIGGER across screen width, centered) ──
      const logo = logoRef.current;
      const targetLogoW = Math.min(w * 0.84, 460);
      const logoScale = Math.max(4, Math.floor(targetLogoW / logo.width));
      const logoW = logo.width * logoScale;
      const logoX = Math.round((w - logoW) / 2);
      const logoY = Math.max(50, Math.round(h * 0.075));
      renderPixels(ctx, logo, logoX, logoY, logoScale);

      // ── 6. Draw Characters (Hero BIG, Friends 0-3 SMALLER) ──
      // Hero scale is large and commanding
      const heroScale = Math.min(8, Math.max(5, Math.floor(Math.min(w, h * 0.75) / 62)));
      const heroW = 16 * heroScale;
      const heroH = 24 * heroScale;
      const heroY = groundY - heroH + Math.round(heroScale * 1.5);

      // Friends scale is visibly smaller (~62% of hero)
      const friendScale = Math.max(3, Math.round(heroScale * 0.62));
      const friendW = 16 * friendScale;
      const friendH = 24 * friendScale;
      const friendY = groundY - friendH + Math.round(friendScale * 1.5);

      const numFriends = friendsDataRef.current.length;
      let heroX: number;

      if (numFriends === 0) {
        heroX = Math.round((w - heroW) / 2);
      } else if (numFriends === 1) {
        heroX = Math.round(w * 0.24 - heroW / 2);
      } else if (numFriends === 2) {
        heroX = Math.round(w * 0.20 - heroW / 2);
      } else {
        // 3 friends
        heroX = Math.round(w * 0.17 - heroW / 2);
      }

      // Draw Main Hero
      const heroFrame = heroAnimRef.current?.getFrame() ?? generateCharacterPixels(heroPartsRef.current);
      renderPixels(ctx, heroFrame, heroX, heroY, heroScale);

      // Hero name banner
      const heroNameScale = Math.max(2, Math.floor(heroScale * 0.45));
      const heroNameW = measurePixelText(player.name, heroNameScale);
      const heroNameX = Math.round(heroX + (heroW - heroNameW) / 2);
      const heroPillY = heroY + heroH + 6;
      const heroPillH = 5 * heroNameScale + 8;
      const heroPillW = heroNameW + 12;
      const heroPillX = heroNameX - 6;

      ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
      ctx.fillRect(heroPillX, heroPillY, heroPillW, heroPillH);
      ctx.strokeStyle = '#c9a654';
      ctx.lineWidth = 1;
      ctx.strokeRect(heroPillX, heroPillY, heroPillW, heroPillH);
      renderPixelText(ctx, player.name, heroNameX, heroPillY + 4, heroNameScale, '#ffe066');

      // Store Hero hit area
      const pad = 20;
      hitAreasRef.current.hero = {
        x: heroX - pad,
        y: heroY - pad,
        w: heroW + pad * 2,
        h: heroH + 34 + pad * 2,
      };

      // Draw Friends (0 to 3)
      const friendsHits: Array<{
        name: string;
        buffIndex?: number;
        code?: string;
        x: number;
        y: number;
        w: number;
        h: number;
      }> = [];

      friendsDataRef.current.forEach((friend, idx) => {
        let friendX: number;
        if (numFriends === 1) {
          friendX = Math.round(w * 0.76 - friendW / 2);
        } else if (numFriends === 2) {
          const positions = [w * 0.58, w * 0.85];
          friendX = Math.round((positions[idx] ?? w * 0.75) - friendW / 2);
        } else {
          // 3 friends
          const positions = [w * 0.48, w * 0.69, w * 0.89];
          friendX = Math.round((positions[idx] ?? w * 0.75) - friendW / 2);
        }

        const friendFrame = friend.animator.getFrame();
        renderPixels(ctx, friendFrame, friendX, friendY, friendScale);

        // Friend name banner — enlarged font scale (at least scale 2)
        const friendNameScale = Math.max(2, Math.floor(heroScale * 0.42));
        const friendNameW = measurePixelText(friend.info.name, friendNameScale);
        const friendNameX = Math.round(friendX + (friendW - friendNameW) / 2);
        const pillY = friendY + friendH + 6;
        const pillH = 5 * friendNameScale + 8;
        const pillW = friendNameW + 10;
        const pillX = friendNameX - 5;

        ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
        ctx.fillRect(pillX, pillY, pillW, pillH);
        ctx.strokeStyle = 'rgba(201, 166, 84, 0.6)';
        ctx.lineWidth = 1;
        ctx.strokeRect(pillX, pillY, pillW, pillH);

        renderPixelText(
          ctx,
          friend.info.name,
          friendNameX,
          pillY + 4,
          friendNameScale,
          '#ffffff',
        );

        friendsHits.push({
          name: friend.info.name,
          buffIndex: friend.info.buffIndex ?? ((idx + 1) as 1 | 2 | 3),
          code: friend.info.code,
          x: friendX - pad,
          y: friendY - pad,
          w: friendW + pad * 2,
          h: friendH + 34 + pad * 2,
        });
      });

      hitAreasRef.current.friends = friendsHits;

      // ── 7. Draw payment coin / hourglass (UPPER THIRD OF SCREEN) ──
      if (payment.required) {
        const coinScale = Math.max(6, Math.round(heroScale * 1.25));
        const coinBob = Math.sin(timestamp * 0.005) * 8;
        const centerY = Math.round(h * 0.33);

        if (payment.status === 'submitted') {
          // Show hourglass
          const hg = hourglassRef.current;
          const hgW = hg.width * coinScale;
          const hgH = hg.height * coinScale;
          const hgX = Math.round(w / 2 - hgW / 2);
          const hgY = Math.round(centerY - hgH / 2 + coinBob);
          renderPixels(ctx, hg, hgX, hgY, coinScale);

          // "WAITING..." text
          const waitScale = Math.max(2, Math.floor(coinScale * 0.32));
          const waitText = t(language, 'waiting');
          const waitW = measurePixelText(waitText, waitScale);
          const waitX = Math.round(w / 2 - waitW / 2);
          const waitY = hgY + hgH + 8;
          ctx.fillStyle = 'rgba(10, 14, 28, 0.75)';
          ctx.fillRect(waitX - 6, waitY - 2, waitW + 12, 14 * waitScale);
          renderPixelText(ctx, waitText, waitX, waitY, waitScale, '#ffe066');

          hitAreasRef.current.coin = { x: hgX - pad, y: hgY - pad, w: hgW + pad * 2, h: hgH + 20 + pad * 2 };
        } else if (payment.status === 'pending') {
          // Show rotating coin
          const coinW = 8 * coinScale;
          const coinFrame = coinAnimRef.current?.getFrame() ?? getCoinFrames()[0]!;
          const coinX = Math.round(w / 2 - coinW / 2);
          const coinY = Math.round(centerY - coinW / 2 + coinBob);

          // Glowing halo aura around coin
          const glowAlpha = 0.22 + Math.sin(timestamp * 0.006) * 0.12;
          ctx.fillStyle = `rgba(255, 224, 102, ${glowAlpha})`;
          ctx.fillRect(coinX - 8, coinY - 8, coinW + 16, coinW + 16);

          renderPixels(ctx, coinFrame, coinX, coinY, coinScale);

          // "TAP" hint
          const tapScale = Math.max(2, Math.floor(coinScale * 0.35));
          const tapText = t(language, 'tap');
          const tapW = measurePixelText(tapText, tapScale);
          const tapX = Math.round(w / 2 - tapW / 2);
          const tapY = coinY + coinW + 8;
          ctx.fillStyle = 'rgba(10, 14, 28, 0.75)';
          ctx.fillRect(tapX - 6, tapY - 2, tapW + 12, 14 * tapScale);
          renderPixelText(ctx, tapText, tapX, tapY, tapScale, '#ffe066');

          hitAreasRef.current.coin = { x: coinX - pad, y: coinY - pad, w: coinW + pad * 2, h: coinW + 24 + pad * 2 };
        }
      }

      // ── 8. Battle Effects (day 22: arrows+lightning, day 7: dragons+fire) ──
      if (battleTimer) {
        const phase = battleTimer.phase;

        // ── First Battle Day: Arrows + Lightning ──
        if (phase === 'first_battle_day') {
          // Spawn arrows periodically
          if (Math.random() < delta * 2) {
            arrowsRef.current.push({
              x: -40,
              y: Math.random() * h * 0.65 + h * 0.08,
              speed: 180 + Math.random() * 120,
              scale: Math.max(2, Math.floor(scale * 0.5)),
            });
          }
          // Update & draw arrows
          arrowsRef.current = arrowsRef.current.filter(a => a.x < w + 50);
          for (const arrow of arrowsRef.current) {
            arrow.x += arrow.speed * delta;
            renderPixels(ctx, arrowRef.current, Math.round(arrow.x), Math.round(arrow.y), arrow.scale);
          }

          // Lightning flashes
          lightningTimerRef.current += delta;
          if (lightningTimerRef.current > 2 + Math.random() * 3) {
            lightningTimerRef.current = 0;
            lightningAlphaRef.current = 0.6;
            // Draw lightning bolt at random x
            const lx = Math.random() * w * 0.8 + w * 0.1;
            const ls = Math.max(3, Math.floor(scale * 0.8));
            renderPixels(ctx, lightningRef.current, Math.round(lx), Math.round(h * 0.05), ls);
          }
          if (lightningAlphaRef.current > 0) {
            ctx.fillStyle = `rgba(200, 220, 255, ${lightningAlphaRef.current})`;
            ctx.fillRect(0, 0, w, h);
            lightningAlphaRef.current = Math.max(0, lightningAlphaRef.current - delta * 3);
          }
        }

        // ── Final Battle Day: Dragons + Fire ──
        if (phase === 'final_battle_day') {
          // Spawn dragons if needed
          if (dragonsRef.current.length < 2) {
            dragonsRef.current.push({
              x: -80,
              yRatio: 0.12 + Math.random() * 0.18,
              speedX: 25 + Math.random() * 20,
              scale: Math.max(3, Math.floor(scale * 0.7)),
              frame: 0,
              timer: 0,
            });
          }
          // Update & draw dragons
          for (const dragon of dragonsRef.current) {
            dragon.x += dragon.speedX * delta;
            dragon.timer += delta;
            if (dragon.timer >= 0.2) {
              dragon.timer = 0;
              dragon.frame = (dragon.frame + 1) % 3;
            }
            if (dragon.x > w + 100) {
              dragon.x = -100;
              dragon.yRatio = 0.10 + Math.random() * 0.20;
            }
            const dragonY = h * dragon.yRatio + Math.sin(timestamp * 0.002) * 10;
            const dFrame = dragonFramesRef.current[dragon.frame] ?? dragonFramesRef.current[0]!;
            renderPixels(ctx, dFrame, Math.round(dragon.x), Math.round(dragonY), dragon.scale);
          }

          // Fire particles from ground
          if (Math.random() < delta * 6) {
            fireParticlesRef.current.push({
              x: Math.random() * w,
              y: groundY,
              life: 0,
              maxLife: 0.8 + Math.random() * 0.6,
              speed: 30 + Math.random() * 40,
              scale: Math.max(2, Math.floor(scale * 0.4)),
            });
          }
          fireParticlesRef.current = fireParticlesRef.current.filter(p => p.life < p.maxLife);
          for (const particle of fireParticlesRef.current) {
            particle.life += delta;
            particle.y -= particle.speed * delta;
            const alpha = 1 - particle.life / particle.maxLife;
            ctx.globalAlpha = alpha;
            renderPixels(ctx, fireParticleRef.current, Math.round(particle.x), Math.round(particle.y), particle.scale);
          }
          ctx.globalAlpha = 1;
        }
      }

      // ── 9. Battle Timer (replaces BATTLEFIELD banner) ──
      if (battleTimer) {
        // Calculate countdown
        const now = Date.now();
        const target = new Date(battleTimer.targetDate).getTime();
        const diff = Math.max(0, target - now);
        const totalSecs = Math.floor(diff / 1000);
        const days = Math.floor(totalSecs / 86400);
        const hrs = Math.floor((totalSecs % 86400) / 3600);
        const mins = Math.floor((totalSecs % 3600) / 60);
        const secs = totalSecs % 60;

        // Line 2: timer in format {дні}d {години}:{хвилини}:{секунди}
        const timeStr = `${days}d ${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

        // Line 1: event title
        let titleText: string;
        let titleColor: string;
        let timerColor: string;

        switch (battleTimer.phase) {
          case 'waiting_first_battle':
            titleText = t(language, 'firstBattleEvent');
            titleColor = '#7fa3cf';
            timerColor = '#ffffff';
            break;
          case 'first_battle_day':
            titleText = t(language, 'firstBattleEvent');
            titleColor = '#ff4444';
            timerColor = '#ffe066';
            break;
          case 'waiting_final_battle':
            titleText = t(language, 'finalBattleEvent');
            titleColor = '#ff8855';
            timerColor = '#ffffff';
            break;
          case 'final_battle_day':
            titleText = t(language, 'finalBattleEvent');
            titleColor = '#ff4444';
            timerColor = '#ffe066';
            break;
          case 'sacrifice_pending':
            titleText = t(language, 'sacrificeEvent');
            titleColor = '#ffe066';
            timerColor = '#ffffff';
            break;
          case 'sacrifice_processing':
            titleText = t(language, 'sacrificeEvent');
            titleColor = '#8b8b9e';
            timerColor = '#8b8b9e';
            break;
          default:
            titleText = t(language, 'firstBattleEvent');
            titleColor = '#7fa3cf';
            timerColor = '#ffffff';
        }

        // 1) Increased font size: at least 3 (was 2), responsive up to 5
        let timerScale = Math.min(5, Math.max(3, Math.floor(scale * 0.85)));
        // Ensure text fits within 88% of screen width so it never overflows
        while (
          timerScale > 2 &&
          (measurePixelText(titleText, timerScale) > w * 0.88 ||
           measurePixelText(timeStr, timerScale) > w * 0.88)
        ) {
          timerScale--;
        }

        const titleW = measurePixelText(titleText, timerScale);
        const timeW = measurePixelText(timeStr, timerScale);
        const titleH = 5 * timerScale;
        const timeH = 5 * timerScale;
        const lineGap = Math.max(4, timerScale * 2);

        const padX = 16;
        const padY = 10;
        const pillW = Math.max(titleW, timeW) + padX * 2;
        const pillH = titleH + lineGap + timeH + padY * 2;
        const pillX = Math.round(w / 2 - pillW / 2);

        // Position neatly above bottom safe area
        const bottomMargin = Math.max(16, Math.round(h * 0.035));
        const pillY = Math.round(h - pillH - bottomMargin);

        // Background pill
        ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
        ctx.fillRect(pillX, pillY, pillW, pillH);

        // Subtle themed border
        ctx.strokeStyle =
          battleTimer.phase === 'sacrifice_pending'
            ? 'rgba(255, 224, 102, 0.5)'
            : battleTimer.phase === 'first_battle_day' || battleTimer.phase === 'final_battle_day'
            ? 'rgba(255, 68, 68, 0.5)'
            : 'rgba(201, 166, 84, 0.4)';
        ctx.lineWidth = 2;
        ctx.strokeRect(pillX, pillY, pillW, pillH);

        // Line 1: Title
        const titleX = Math.round(w / 2 - titleW / 2);
        const titleY = pillY + padY;
        renderPixelText(ctx, titleText, titleX, titleY, timerScale, titleColor);

        // Line 2: Countdown
        const timeX = Math.round(w / 2 - timeW / 2);
        const timeY = titleY + titleH + lineGap;
        renderPixelText(ctx, timeStr, timeX, timeY, timerScale, timerColor);

        // Store timer hit area
        hitAreasRef.current.timer = { x: pillX, y: pillY, w: pillW, h: pillH };
      }

      // ── Top Header Controls ──
      const langScale = Math.min(4, Math.max(3, Math.floor(scale * 0.6)));
      const textH = 5 * langScale;
      const padX = Math.max(10, langScale * 3);
      const padY = Math.max(6, langScale * 2);
      const pillMargin = Math.max(14, Math.round(w * 0.035));
      const pillMarginTop = Math.max(14, Math.round(h * 0.025));
      const hitPad = 6;

      // ── 10. Logout Button (top-left corner) ──
      const logoutText = t(language, 'logoutBtn');
      const logoutW = measurePixelText(logoutText, langScale);
      const logoutPillW = logoutW + padX * 2;
      const logoutPillH = textH + padY * 2;
      const logoutPillX = pillMargin;
      const logoutPillY = pillMarginTop;

      // Background pill
      ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
      ctx.fillRect(logoutPillX, logoutPillY, logoutPillW, logoutPillH);

      // Border
      ctx.strokeStyle = 'rgba(220, 80, 80, 0.5)';
      ctx.lineWidth = 2;
      ctx.strokeRect(logoutPillX, logoutPillY, logoutPillW, logoutPillH);

      // Draw text
      renderPixelText(ctx, logoutText, logoutPillX + padX, logoutPillY + padY, langScale, '#ff7070');

      // Store logout button hit area
      hitAreasRef.current.logoutBtn = {
        x: logoutPillX - hitPad,
        y: logoutPillY - hitPad,
        w: logoutPillW + hitPad * 2,
        h: logoutPillH + hitPad * 2,
      };

      // ── 11. Info Button (top-center) ──
      const infoText = t(language, 'infoBtn');
      const infoW = measurePixelText(infoText, langScale);
      const infoPillW = infoW + padX * 2;
      const infoPillH = textH + padY * 2;
      const infoPillX = Math.round((w - infoPillW) / 2);
      const infoPillY = pillMarginTop;

      // Background pill
      ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
      ctx.fillRect(infoPillX, infoPillY, infoPillW, infoPillH);

      // Border
      ctx.strokeStyle = 'rgba(201, 166, 84, 0.4)';
      ctx.lineWidth = 2;
      ctx.strokeRect(infoPillX, infoPillY, infoPillW, infoPillH);

      // Draw text
      renderPixelText(ctx, infoText, infoPillX + padX, infoPillY + padY, langScale, '#ffe066');

      // Store info button hit area
      hitAreasRef.current.infoBtn = {
        x: infoPillX - hitPad,
        y: infoPillY - hitPad,
        w: infoPillW + hitPad * 2,
        h: infoPillH + hitPad * 2,
      };

      // ── 12. Language Toggle (top-right corner) ──
      const uaText = 'UA';
      const enText = 'EN';
      const sepText = '|';
      const uaW = measurePixelText(uaText, langScale);
      const sepW = measurePixelText(sepText, langScale);
      const enW = measurePixelText(enText, langScale);
      const langGap = langScale * 2;
      const contentW = uaW + langGap + sepW + langGap + enW;
      const pillW = contentW + padX * 2;
      const pillH = textH + padY * 2;
      const pillX = Math.round(w - pillW - pillMargin);
      const pillY = pillMarginTop;

      // Background pill
      ctx.fillStyle = 'rgba(10, 14, 28, 0.85)';
      ctx.fillRect(pillX, pillY, pillW, pillH);

      // Border
      ctx.strokeStyle = 'rgba(201, 166, 84, 0.4)';
      ctx.lineWidth = 2;
      ctx.strokeRect(pillX, pillY, pillW, pillH);

      // Draw text
      let lx = pillX + padX;
      const textY = pillY + padY;

      // UA
      renderPixelText(ctx, uaText, lx, textY, langScale, language === 'uk' ? '#ffe066' : '#8b8b9e');
      lx += uaW + langGap;

      // |
      renderPixelText(ctx, sepText, lx, textY, langScale, '#555566');
      lx += sepW + langGap;

      // EN
      renderPixelText(ctx, enText, lx, textY, langScale, language === 'en' ? '#ffe066' : '#8b8b9e');

      // Store language toggle hit area with generous touch padding for mobile
      hitAreasRef.current.langToggle = {
        x: pillX - hitPad,
        y: pillY - hitPad,
        w: pillW + hitPad * 2,
        h: pillH + hitPad * 2,
      };

      animFrameRef.current = requestAnimationFrame(render);
    },
    [player, payment, scale, battleTimer, language],
  );

  // Start/stop game loop
  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(render);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [render]);

  // Shared hit-test logic
  const processHit = useCallback(
    (x: number, y: number): boolean => {
      if (isModalLoading) return false;
      const { hero, friends, coin, timer, langToggle, infoBtn, logoutBtn } = hitAreasRef.current;

      // Check info button
      if (infoBtn && x >= infoBtn.x && x <= infoBtn.x + infoBtn.w && y >= infoBtn.y && y <= infoBtn.y + infoBtn.h) {
        onInfoClick();
        return true;
      }

      // Check logout button
      if (logoutBtn && x >= logoutBtn.x && x <= logoutBtn.x + logoutBtn.w && y >= logoutBtn.y && y <= logoutBtn.y + logoutBtn.h) {
        onLogoutClick();
        return true;
      }

      // Check language toggle first (highest priority — small target)
      if (x >= langToggle.x && x <= langToggle.x + langToggle.w && y >= langToggle.y && y <= langToggle.y + langToggle.h) {
        onLanguageToggle();
        return true;
      }

      // Check coin
      if (payment.required && payment.status === 'pending') {
        if (x >= coin.x && x <= coin.x + coin.w && y >= coin.y && y <= coin.y + coin.h) {
          onCoinClick();
          return true;
        }
      }

      // Check timer
      if (x >= timer.x && x <= timer.x + timer.w && y >= timer.y && y <= timer.y + timer.h) {
        onTimerClick();
        return true;
      }

      // Check hero
      if (x >= hero.x && x <= hero.x + hero.w && y >= hero.y && y <= hero.y + hero.h) {
        onHeroClick();
        return true;
      }

      // Check friends
      for (const friend of friends) {
        if (x >= friend.x && x <= friend.x + friend.w && y >= friend.y && y <= friend.y + friend.h) {
          onFriendClick(friend.name, friend.buffIndex, friend.code);
          return true;
        }
      }

      return false;
    },
    [payment, onHeroClick, onFriendClick, onCoinClick, onTimerClick, onLanguageToggle, onInfoClick, onLogoutClick, isModalLoading],
  );

  // Handle canvas clicks
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      processHit(e.clientX - rect.left, e.clientY - rect.top);
    },
    [processHit],
  );

  // Handle touch events
  const handleTouch = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const touch = e.touches[0];
      if (!touch) return;

      const rect = canvas.getBoundingClientRect();
      processHit(touch.clientX - rect.left, touch.clientY - rect.top);
    },
    [processHit],
  );

  // Handle hover cursor
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (isModalLoading) {
        canvas.style.cursor = 'wait';
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const { hero, friends, coin, timer, langToggle, infoBtn, logoutBtn } = hitAreasRef.current;
      const isCoin =
        payment.required &&
        payment.status === 'pending' &&
        x >= coin.x &&
        x <= coin.x + coin.w &&
        y >= coin.y &&
        y <= coin.y + coin.h;

      const isHero =
        x >= hero.x && x <= hero.x + hero.w && y >= hero.y && y <= hero.y + hero.h;

      const isFriend = friends.some(
        (f) => x >= f.x && x <= f.x + f.w && y >= f.y && y <= f.y + f.h,
      );

      const isTimer =
        x >= timer.x && x <= timer.x + timer.w && y >= timer.y && y <= timer.y + timer.h;

      const isLang =
        x >= langToggle.x && x <= langToggle.x + langToggle.w && y >= langToggle.y && y <= langToggle.y + langToggle.h;

      const isInfo =
        Boolean(infoBtn) &&
        x >= infoBtn.x &&
        x <= infoBtn.x + infoBtn.w &&
        y >= infoBtn.y &&
        y <= infoBtn.y + infoBtn.h;

      const isLogout =
        Boolean(logoutBtn) &&
        x >= logoutBtn.x &&
        x <= logoutBtn.x + logoutBtn.w &&
        y >= logoutBtn.y &&
        y <= logoutBtn.y + logoutBtn.h;

      canvas.style.cursor =
        isCoin || isHero || isFriend || isTimer || isLang || isInfo || isLogout ? 'pointer' : 'default';
    },
    [payment, isModalLoading],
  );

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouch}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        imageRendering: 'pixelated',
        touchAction: 'none',
      }}
    />
  );
}
